import React, { memo, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";

const PlutoLeftSidebar = memo(({ categories, activeCategory, onCategoryClick }) => {
  const sidebarRef = useRef(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [isFixed, setIsFixed] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [sidebarTop, setSidebarTop] = useState(0);
  
  const nonFeaturedCategories = categories.filter(cat => cat.type !== "FEATURED");

  const throttle = (func, limit) => {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const categoriesElement = document.querySelector('.pluto-categories-container');
      const featuredElement = document.querySelector('.pluto-featured-wrapper');
      const homePageContainer = document.querySelector('.pluto-homepage-container');
      const header = document.querySelector('header');
      
      if (categoriesElement && featuredElement && homePageContainer) {
        const categoriesRect = categoriesElement.getBoundingClientRect();
        const featuredRect = featuredElement.getBoundingClientRect();
        const homePageRect = homePageContainer.getBoundingClientRect();
        
        const featuredBottom = featuredRect.bottom;
        const currentScrollY = window.scrollY;
        const categoriesBottomY = categoriesElement.getBoundingClientRect().bottom;
        const viewportBottomY = window.innerHeight;
        const isCategoriesBottomInView = categoriesBottomY <= viewportBottomY;
        setIsAtBottom(isCategoriesBottomInView);

        const homePageBottom = homePageRect.bottom;
        const bottomOfViewport = window.innerHeight;
        const approachingEnd = homePageBottom - bottomOfViewport < 0;
        
        const headerHeight = header ? header.offsetHeight : 0;

        if (categoriesRect.top <= headerHeight && featuredBottom <= headerHeight &&
            !isCategoriesBottomInView) {
          setIsFixed(true);
          setSidebarTop(0);
          if (header && sidebarRef.current) {
            sidebarRef.current.style.setProperty('--header-height', `${headerHeight}px`);
          }
        } else if (isCategoriesBottomInView) {
          setIsFixed(false);
          const categoriesTop = categoriesElement.getBoundingClientRect().top + window.pageYOffset;
          const categoriesHeight = categoriesElement.offsetHeight;
          setSidebarTop(categoriesTop + categoriesHeight - window.innerHeight);
        } else {
          setIsFixed(false);
          setSidebarTop(0);
        }
        
        lastScrollY = currentScrollY;
      }
    };
    const throttledHandleScroll = throttle(handleScroll, 50);
    
    window.addEventListener('scroll', throttledHandleScroll);
    
    const header = document.querySelector('header');
    if (header && sidebarRef.current) {
      const headerHeight = header.offsetHeight;
      sidebarRef.current.style.setProperty('--header-height', `${headerHeight}px`);
    }
    
    setTimeout(handleScroll, 200);
    
    const handleResize = () => {
      const header = document.querySelector('header');
      if (header && sidebarRef.current) {
        const headerHeight = header.offsetHeight;
        sidebarRef.current.style.setProperty('--header-height', `${headerHeight}px`);
      }
      handleScroll();
    };
    
    window.addEventListener('resize', throttle(handleResize, 100));
    
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (activeCategory && sidebarRef.current) {
      const activeElement = sidebarRef.current.querySelector(`[data-category="${activeCategory}"]`);
      if (activeElement) {
        const container = sidebarRef.current.querySelector('.pluto-left-sidebar-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = activeElement.getBoundingClientRect();
          const offset = elementRect.top - containerRect.top;
          
          if (offset < 0 || offset > containerRect.height) {
            container.scrollTop += offset;
          }
        }
      }
    }
  }, [activeCategory]);

  const handleCategoryClick = (categoryKey) => {
    if (onCategoryClick) {
      const categoryElement = window.__plutoCategoryRefs?.[categoryKey];
      if (categoryElement) {
        const offset = 100;
        const elementPosition = categoryElement.getBoundingClientRect().top + window.pageYOffset - offset;
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo({
          top: Math.min(elementPosition, maxScroll),
          behavior: "smooth"
        });
      }
      onCategoryClick(categoryKey);
    }
  };

  return (
    <div className="pluto-left-sidebar" ref={sidebarRef}>
      <div
        className={`pluto-left-sidebar-container${isFixed ? ' fixed' : ''}${isAtBottom ? ' at-bottom' : ''}`}
        style={
          isAtBottom && sidebarTop
            ? { top: `${sidebarTop}px` }
            : {}
        }
      >
        {nonFeaturedCategories.map((category, index) => {
          const isActive = category.key === activeCategory;
          const isHovered = category.key === hoveredCategory;
          
          return (
            <div 
              key={`sidebar-${category.key || index}`}
              className={`pluto-sidebar-item ${isActive ? ' active' : ''} ${isHovered ? ' hovered' : ''}`}
              data-category={category.key}
              onClick={() => handleCategoryClick(category.key)}
              onMouseEnter={() => setHoveredCategory(category.key)}
              onMouseLeave={() => setHoveredCategory(null)}
            >
              <span className="category-name">
                {category.category_name || category.name || category.title}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
});

PlutoLeftSidebar.displayName = "PlutoLeftSidebar";
export default PlutoLeftSidebar;
